import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import Jwt from "@/common/utils/jwt";
import { API_URL } from "@/config/config";
import {
  interpretarErro,
  textoDoErro,
} from "@/common/erros/erro.service";
import { apresentarErro } from "@/common/erros/apresentacao";
import { TipoErro, marcarComoApresentado } from "@/common/erros/catalogo";
import { opcoesToast } from "@/common/erros/toast.options";

// init() é chamado por setHeader() a cada request, então o interceptor de
// resposta precisa de guarda: sem ela ele se empilharia a cada chamada.
let interceptorsRegistrados = false;

axios.interceptors.request.use((config) => {
  const indexInterrogacao = config.url.indexOf("?");
  const indexUltimaBarra = config.url.lastIndexOf("/");
  if (indexInterrogacao < indexUltimaBarra && indexInterrogacao >= 0) {
    config.url = config.url.substring(0, config.url.length - 1);
  }
  return config;
});

// 401 em que o 401 NÃO significa sessão vencida: credencial errada no login/MFA,
// recuperação de senha e as telas públicas do pagador (link de pagamento), que
// não dependem do token do portal.
const ROTAS_SEM_SESSAO = [
  "autenticacao/autenticar",
  "autenticacao/validar-mfa",
  "autenticacao/reenviar-mfa",
  "usuario/solicitar-reset-senha",
  "usuario/reset-senha",
  "usuario/obter-reset-token",
  "pagamento/efetuar-white-label",
  "pagamento/obter-dados-intencao-white-label",
  "pagamento/obter-dados-aguardando-confirmacao",
  "pagamento/obter-status-pagamento-finalizado",
  "pagamento/obter-dados-pagamento-negado",
];

const ehSessaoExpirada = (err) => {
  if (!err || !err.response || err.response.status !== 401) return false;
  if (!Jwt.buscarToken()) return false;

  const url = (err.config && err.config.url) || "";
  return !ROTAS_SEM_SESSAO.some((rota) => url.includes(rota));
};

let sessaoEncerrada = false;

/**
 * Token vencido: toda chamada da tela volta 401 ao mesmo tempo.
 *
 * Antes o 401 era rejeitado sem aviso nenhum (o ApiService pulava o toast) e a
 * sessão morta continuava aberta até a próxima troca de rota -- o usuário
 * clicava em Salvar e nada acontecia. Aqui a sessão é encerrada uma única vez e
 * a promise fica pendente de propósito: a página já está indo para o login, e
 * nenhuma tela deve apresentar erro por uma chamada que não importa mais.
 */
function encerrarSessaoExpirada() {
  if (!sessaoEncerrada) {
    sessaoEncerrada = true;
    try {
      // O Login.vue lê esta marca e explica por que o usuário voltou para lá.
      window.sessionStorage.setItem("sessaoExpirada", "1");
    } catch (e) {
      // sessionStorage bloqueado: o usuário só não verá o aviso no login
    }
    // require tardio: importar o store no topo fecha o ciclo
    // store -> autenticacao.module -> autenticacao.service -> api.service -> store.
    const store = require("@/store/Store").default;
    const { LIMPAR_DADOS_AUTENTICACAO } = require("@/store/autenticacao.module");
    store.commit(LIMPAR_DADOS_AUTENTICACAO);
  }
  return new Promise(() => {});
}

function registrarInterceptorDeResposta() {
  if (interceptorsRegistrados) return;
  interceptorsRegistrados = true;

  // Sem timeout global de propósito: o POST do pagamento depende da adquirente,
  // e um timeout no cliente levaria o pagador a pagar de novo uma cobrança que
  // pode ter sido feita. Ver README, "Tratamento de Erros".

  axios.interceptors.response.use(
    (response) => response,
    (err) => {
      if (ehSessaoExpirada(err)) return encerrarSessaoExpirada();

      // Anexa a interpretação ao próprio erro, para quem faz catch não precisar
      // reimplementar o parsing.
      try {
        err.valorem = interpretarErro(err);
      } catch (e) {
        // nunca deixar o tratador de erro derrubar o fluxo
      }
      return Promise.reject(err);
    }
  );
}

const ehNaoAutorizado = (err) =>
  !!(err && err.response && err.response.status == 401);

/**
 * Service to call HTTP request via Axios
 */
const ApiService = {
  verifyData(dataFormated) {
    let camposInvalidos = [];

    let verifyDataObj = dataFormated ? dataFormated : this.data;

    for (let i = 0; i < verifyDataObj.length; i++) {
      if (verifyDataObj[i].idItem && verifyDataObj[i].idItem != "") {
        for (const key in verifyDataObj[i]) {
          if (Object.hasOwnProperty.call(verifyDataObj[i], key)) {
            for (const x in verifyDataObj[i][key]) {
              if (Object.hasOwnProperty.call(verifyDataObj[i][key], x)) {
                if (x == "valido" && verifyDataObj[i][key].valido == false) {
                  camposInvalidos.push(true);
                }
              }
            }
          }
        }
      }
    }

    return camposInvalidos.length > 0 ? false : true;
  },
  init() {
    Vue.use(VueAxios, axios);
    registrarInterceptorDeResposta();

    this.setbaseURL(API_URL);
  },
  setbaseURL(url) {
    Vue.axios.defaults.baseURL = url;
  },
  getbaseURL() {
    this.init();
    return Vue.axios.defaults.baseURL;
  },
  // Mantido por compatibilidade: devolve o texto já higienizado (sem conteúdo
  // técnico) em vez dos antigos fallbacks genéricos. Nas telas, prefira
  // this.tratarErro(err), que também destaca o campo e escolhe toast ou modal.
  getErrorMessage(err) {
    return textoDoErro((err && err.valorem) || interpretarErro(err));
  },
  // Casca fina sobre o interpretador central. A decisão do que mostrar, e a
  // guarda que impede conteúdo técnico de chegar à tela, vivem em src/common/erros.
  // Marca o erro como apresentado: o tratarErro da tela só acrescenta o destaque
  // do campo, sem repetir a mensagem.
  showErrors(err) {
    const erro = (err && err.valorem) || interpretarErro(err);
    if (erro.tipo === TipoErro.CANCELADO) return erro;

    apresentarErro(erro);
    marcarComoApresentado(err);
    return erro;
  },
  showSuccess(message = "A ação foi executada com sucesso!") {
    Vue.$toast.success(message, opcoesToast());
  },

  /**
   * Set the default HTTP request headers
   */
  setHeader() {
    this.init();
    Vue.axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${Jwt.buscarToken()}`;
    this.setbaseURL(API_URL);
    Vue.axios.defaults.headers.common["Content-Language"] = "pt-BR";
  },

  // Não mostra erro nenhum: quem chama query() precisa de .catch com tratarErro.
  query(resource, params) {
    this.setHeader();
    return Vue.axios.get(resource, params);
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  get(resource, slug = "", config = {}) {
    this.setHeader();
    return new Promise((resolve, reject) => {
      Vue.axios
      .get(`${resource}/${slug}`)
      .then((retorno) => {
        resolve(retorno);
      })
      .catch((err) => {
        reject(err)
        if (ehNaoAutorizado(err)) return;

        if (!config.noToast) {
          this.showErrors(err);
        }

      });
    });
  },
  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  post(resource, params, config = {}, file, noMultiple, formDataUp) {
    this.setHeader();

    if (formDataUp) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .post(`${resource}`, formDataUp, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((retorno) => {
            if (!config.noToast) this.showSuccess();

            resolve(retorno);
          })
          .catch((err) => {
            reject(err);
            if (ehNaoAutorizado(err)) return;
            this.showErrors(err);

          });
      })
    }

    if (file) {
      let formData = new FormData();

      if (!noMultiple) {
        for (var i = 0; i < file.length; i++) {
          let fileItem = file[i];
          formData.append("arquivos", fileItem);
        }
      } else {
        formData.append("arquivo", file);
      }

      // formData.append("arquivos", file);
      return new Promise((resolve, reject) => {
        Vue.axios
          .post(`${resource}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((retorno) => {
            if (!config.noToast) this.showSuccess();

            resolve(retorno);
          })
          .catch((err) => {
            reject(err);
            if (ehNaoAutorizado(err)) return;
            this.showErrors(err);

          });
        });
    } else {
      return new Promise((resolve, reject) => {
          Vue.axios
          .post(`${resource}`, params, config)
          .then((retorno) => {
            // Com `?.`: resposta de sucesso sem `data.data` (ex.: pedido de
            // recuperação de senha) lançava TypeError aqui, caía no catch abaixo
            // e o usuário via uma mensagem de ERRO para uma operação que deu certo.
            const bordero = retorno?.data?.data?.bordero;
            if (!config.noToast && !bordero && !this.verifyData(retorno))
              this.showSuccess();

            if (params && params.forceSuccess) this.showSuccess();
            else if (!config.noToast && !bordero && !this.verifyData(retorno))
              this.showSuccess();

            resolve(retorno);
          })
          .catch((err) => {
            reject(err);
            if (ehNaoAutorizado(err)) return;
            if (!config.noToast) this.showErrors(err);
          });
        });
    }
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    this.setHeader();
    return new Promise((resolve, reject) => {
      Vue.axios
      .put(`${resource}/${slug}`, params)
      .then((retorno) => {
        this.showSuccess();
        resolve(retorno);
      })
      .catch((err) => {
        reject(err);
        if (ehNaoAutorizado(err)) return;
        this.showErrors(err);
      });
    })
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params, config = {}) {
    this.setHeader();
    return new Promise((resolve, reject) => {
      Vue.axios
      .put(`${resource}`, params)
      .then((retorno) => {
        if (!config.noToast && !this.verifyData(retorno)) this.showSuccess();
        resolve(retorno);
      })
      .catch((err) => {
        reject(err);
        if (ehNaoAutorizado(err)) return;
        // Antes o put ignorava o noToast no erro: o invalidar-token do logout,
        // por exemplo, mostrava aviso mesmo pedindo silêncio.
        if (!config.noToast) this.showErrors(err);
      });
    });
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource, params) {
    this.setHeader();
    return new Promise((resolve, reject) => {
      Vue.axios
      .delete(`${resource}`, { data: params })
      .then((retorno) => {
        this.showSuccess();
        resolve(retorno);
      })
      .catch((err) => {
        reject(err);
        if (ehNaoAutorizado(err)) return;
        this.showErrors(err);
      });
    });
  },
  uploadFile(resource, file) {
    let data = new FormData();
    data.append("fileName", file.name);
    data.append("file", file);
    let config = {
      header: {
        "Content-Type": "multipart/form-data",
      },
    };

    // O post() já apresenta o erro e marca como apresentado; repetir o
    // showErrors aqui mostrava a mesma mensagem duas vezes.
    return this.post(resource, data, config);
  },
};

export default ApiService;
